import type { ApiErrorBody } from '../types/meeting';

/**
 * Base URL for the API.
 *
 * Defaults to the relative `/api`, which is correct for both deployments we
 * support:
 *   - production: Nginx serves the app and proxies `/api` on the same origin;
 *   - development: the Vite dev server proxies `/api` to localhost:3001.
 *
 * `VITE_API_URL` only needs setting when the API lives on a different origin.
 */
const API_BASE_URL = (import.meta.env.VITE_API_URL ?? '/api').replace(/\/+$/, '');

/**
 * Absolute URL for an API path, for the rare places that hand the browser a
 * URL instead of fetching — a download link, an `<img src>`. The session
 * cookie rides along on same-origin navigation exactly as it does on fetch.
 */
export function apiUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}

/** Requests are abandoned after this long. */
const REQUEST_TIMEOUT_MS = 10_000;

/**
 * Error carrying the backend's structured failure details.
 *
 * `requestId` is echoed from the response header/body so a user-reported
 * problem can be traced to a specific server log line.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly requestId?: string;
  /** Seconds the server asked us to wait, from `Retry-After` on a 429. */
  readonly retryAfterSeconds?: number;

  constructor(
    message: string,
    status: number,
    requestId?: string,
    retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.requestId = requestId;
    this.retryAfterSeconds = retryAfterSeconds;
  }

  /** True for failures that a retry might plausibly fix. */
  get isRetryable(): boolean {
    return this.status === 0 || this.status === 429 || this.status >= 500;
  }
}

function extractMessage(body: unknown, fallback: string): string {
  if (typeof body !== 'object' || body === null) {
    return fallback;
  }
  const { message } = body as Partial<ApiErrorBody>;
  if (Array.isArray(message)) {
    return message.join('. ');
  }
  if (typeof message === 'string' && message.length > 0) {
    return message;
  }
  return fallback;
}

/**
 * Called when the server says a session has ended, before the error is
 * thrown, so the app is already signed out by the time a caller's `catch`
 * runs. Registered by the auth provider; null on the meeting route, which
 * mounts no provider and has no session to lose.
 */
type UnauthorizedHandler = () => void;
let onUnauthorized: UnauthorizedHandler | null = null;

export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  onUnauthorized = handler;
}

export interface ApiRequestInit extends RequestInit {
  /**
   * Leave the handler alone for this call.
   *
   * Three callers need it, all of them places where a 401 is an answer rather
   * than an expiry: signing in with the wrong password, asking whether anyone
   * is signed in, and confirming a current password. Without it, a mistyped
   * password would sign the operator out of the session they are using.
   */
  skipUnauthorizedHandler?: boolean;
}

/**
 * Thin `fetch` wrapper: JSON in, JSON out, structured errors, hard timeout.
 *
 * Deliberately not a data-fetching library. The app makes three calls; adding
 * TanStack Query here would buy caching we do not need. The abstraction that
 * *does* matter — a single place where auth headers, retries and tracing
 * headers will be attached — is this function.
 */
export async function apiRequest<TResponse>(
  path: string,
  init: ApiRequestInit = {},
): Promise<TResponse> {
  const { skipUnauthorizedHandler = false, ...requestInit } = init;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...requestInit,
      // The operator session is an httpOnly cookie. Same-origin in both
      // deployments, so this is only strictly needed when VITE_API_URL points
      // the app at another origin — which is exactly when forgetting it would
      // be baffling.
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...requestInit.headers,
      },
      signal: controller.signal,
    });
  } catch (error) {
    // Network-level failure: DNS, TLS, offline, or our own timeout. Status 0
    // distinguishes it from an HTTP error the server actually produced.
    const aborted = error instanceof DOMException && error.name === 'AbortError';
    throw new ApiError(
      aborted
        ? 'The server took too long to respond. Check your connection and try again.'
        : 'Could not reach the server. Check your connection and try again.',
      0,
    );
  } finally {
    clearTimeout(timeout);
  }

  const requestId = response.headers.get('x-request-id') ?? undefined;

  if (response.status === 204) {
    return undefined as TResponse;
  }

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    if (response.status === 401 && !skipUnauthorizedHandler) {
      onUnauthorized?.();
    }
    throw new ApiError(
      extractMessage(payload, `Request failed with status ${response.status}`),
      response.status,
      requestId,
      retryAfterFrom(response),
    );
  }

  return payload as TResponse;
}

/** Whole seconds from `Retry-After`; the HTTP-date form is not used here. */
function retryAfterFrom(response: Response): number | undefined {
  const raw = response.headers.get('retry-after');
  if (!raw) {
    return undefined;
  }
  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds >= 0 ? Math.ceil(seconds) : undefined;
}
